import { useState, Suspense, useRef } from "react";
import styled from "styled-components";
import { MapContainer, ScaleControl } from "react-leaflet";
import { useHistory } from "react-router-dom";
import { Map as LeafletMap } from "leaflet";
import { useTranslation } from "react-i18next";

import { CaseLayout } from "src/layouts/CaseLayout";
import { useBoundsWithoutCase, useCaseCreatePredefinedMutation } from "src/hooks";
import { CoordinatesControl } from "src/components/map/CoordinatesControl";
import { MapZoomControls } from "src/components/map/MapZoomControls";
import { CaseCreateForm, CaseCreateFormValues } from "src/forms/CaseCreateForm";
import { Pane } from "src/components/Pane";
import { LayerManagerOnMap } from "src/components/LayerManager/LayerManagerOnMap";
import { MapControlBase } from "src/components/map/MapControlBase";
import { MapSizeObserver } from "src/components/map/MapSizeObserver";
import { OverlayersLegend } from "src/components/OverlayersLegend";
import { caseCreateMachine } from "src/machines/case-create.machine";
import { useMachine } from "@xstate/react";
import { CaseCreatePredefinedForm } from "src/forms/CaseCreatePredefinedForm";
import { BaselayerOnMap } from "src/components/on-map/BaselayerOnMap";
import { ActiveOverlayersOnMap } from "src/components/on-map/ActiveOverlayersOnMap";

interface Props {}

export function CaseCreatePage(props: Props) {
  const { t } = useTranslation();
  const history = useHistory();
  const bounds = useBoundsWithoutCase();

  const mapRef = useRef<L.Map>(null);

  const createPredefinedMutation = useCaseCreatePredefinedMutation();

  const [state, send] = useMachine(caseCreateMachine, {
    actions: {
      exit: () => history.push("/cases"),
      submitPredefined: (context) => {
        createPredefinedMutation
          .mutateAsync({
            name: context.name,
            description: context.description,
            userSrid: parseInt(context.userSrid),
            polygon_frontendSrid: context.polygon_frontendSrid,
          })
          .then(() => send({ type: "CREATION_SUCCESS" }));
      },
      submitNormal: (...args) => console.log("machine action > submitNormal", args),
    },
    // devTools: true,
  });

  return render();

  function render() {
    return (
      <CaseLayout>
        <Styles>
          {renderCaseCreateState()}

          <MapContainer ref={mapRef} bounds={bounds} zoomControl={false} className="map-container">
            <ScaleControl position="bottomleft" imperial={false} />
            <MapZoomControls position="bottomright" />
            <MapSizeObserver />

            <LayerManagerOnMap defaultOpen={true} context="case-create" />

            <MapControlBase position="bottomleft">
              <CoordinatesControl />
            </MapControlBase>

            <MapControlBase position="bottomleft">
              <OverlayersLegend context="case-create" />
            </MapControlBase>

            <BaselayerOnMap />
            <ActiveOverlayersOnMap context="case-create" />
          </MapContainer>
        </Styles>
      </CaseLayout>
    );
  }

  function renderCaseCreateState() {
    if (state.matches("new")) {
      return (
        <Suspense fallback={t<string>("label.loading")}>
          <Pane>
            <CaseCreateForm
              onCancel={() => send({ type: "PREVIOUS" })}
              onSubmit={(values: CaseCreateFormValues) => {
                send({
                  type: "CREATE_PREDEFINED",
                  payload: { ...state.context, ...values },
                });
              }}
              values={state.context}
            />
          </Pane>
        </Suspense>
      );
    } else if (["predefined.step1", "submitting"].some(state.matches)) {
      return (
        <Pane>
          {mapRef.current && (
            <CaseCreatePredefinedForm
              map={mapRef.current}
              onCancel={() => send({ type: "PREVIOUS" })}
              onSubmit={(polygon_frontendSrid: string) =>
                send({
                  type: "NEXT",
                  payload: { ...state.context, polygon_frontendSrid },
                })
              }
              isLoading={createPredefinedMutation.isLoading}
              errors={createPredefinedMutation.error?.response?.data}
            />
          )}
        </Pane>
      );
    } else {
      return null;
    }
  }
}

const Styles = styled.div`
  position: relative;
  width: 100%;
  display: flex;
  flex-direction: row;
  flex: 1;
  overflow: auto;

  .map-container,
  .leaflet-container {
    width: 100%;
    height: 100%;
    flex-grow: 1;
  }
`;
